/**
 * Session-related utility functions specific to session component
 */

import { InputType, PreviewType } from '@/constants'

// Pre-filled field values. Merge under the caller's data, and only when data is sent.
export function getInputFieldDefaults(input: any): Record<string, any> {
  const data: Record<string, any> = {}
  const fields = input?.input_fields
  if (!fields) {
    return data
  }
  for (const [key, field] of Object.entries(fields as Record<string, any>)) {
    if (field && typeof field === 'object' && 'default' in field) {
      data[key] = field.default
    }
  }
  return data
}

/**
 * Check if an input requires item selection
 */
export function isItemSelectionInput(input: any): boolean {
  if (!input) {
    return false
  }

  // Inputs that require item selection
  const itemSelectionTypes = [InputType.Item, InputType.ActiveItem, InputType.SubscriptionItem, InputType.RebillItem]

  return itemSelectionTypes.includes(input.type || input)
}

/**
 * Get CSS class for custom input styling
 */
export function getSessionInputClass(input: any, session: any): string {
  const classes: string[] = ['input-option']

  if (!input) {
    return classes.join(' ')
  }

  // Add button styling classes if available
  if (input.button_styles) {
    if (input.button_styles.is_btn) {
      classes.push('btn')
    }

    if (input.button_styles.style_type) {
      // Map style types to CSS classes
      const styleMap: { [key: number]: string } = {
        1: 'btn-primary',
        2: 'btn-secondary',
        3: 'btn-outline',
        4: 'rounded-full',
        5: 'btn-pill-outline',
        6: 'btn-text-only',
      }

      const styleClass = styleMap[input.button_styles.style_type]
      if (styleClass) {
        classes.push(styleClass)
      }
    }
  }

  // Add session-based classes
  if (session) {
    if (session.is_legacy) {
      classes.push('legacy-input')
    }

    if (session.enhanced_mode) {
      classes.push('enhanced-input')
    }
  }

  return classes.join(' ')
}

// Normalize step slugs/names for URLs and breadcrumbs. Backend can append
// suffixes like "-editor"/"-updated" or wrap names in "(...)" — strip those
// so the URL stays stable across editor/updater variants of the same step.
export function getTrimmedSlug(value: string | null | undefined): string {
  return (value || '')
    .toLowerCase()
    .replaceAll('-editor', '')
    .replaceAll('-updated', '')
    .replaceAll('updated', '')
    .replaceAll('editor', '')
    .replace(/\([^)]*\)/g, '')
    .trim()
}

export function isPreviewSession(session: any): boolean {
  return Number(session?.preview_type) !== PreviewType.None || !!session?.is_step_preview
}

export function getSessionStepInfo(session: any): any {
  if (!session) {
    return null
  }

  return {
    id: session.step_id,
    slug: getTrimmedSlug(session.step_slug),
    name: getTrimmedSlug(session.step_name || session.label),
    can_undo: session.can_undo,
  }
}

/**
 * Check if session is complete
 */
export function isSessionComplete(): boolean {
  // This would typically check session state
  // For now, return false to prevent automatic completion
  return false
}
